import { hardNavigation } from "../../../utils/hard-navigation.util";
import { Injectable } from "@angular/core";
import { VngDialogService } from "@vengreso/angular-components";
import { SocialAuthService } from "@abacritt/angularx-social-login";
import { cloneDeep, forEach } from "lodash";
import { Observable, catchError, map, of, throwError } from "rxjs";
import { Store } from "@ngrx/store";
import { ActivatedRoute } from "@angular/router";
import { detect } from "detect-browser";

import { StripeService } from "src/app/core/core-services/services/stripe.service";
import { environment } from "src/environments/environment";
import { DataService } from "./dataservices/data.service";
import { ExtensionService } from "./extension.service";
import { LoadingService } from "./loader.service";
import { NavigationService } from "./navigation.service";
import { NotificationService } from "./notification.service";
import { SharedDataService } from "./shared-data.service";
import { NotifyService } from "../../menu/not.service";

import { PlanState } from "src/app/core/store/plan/plan.reducer";
import {
  loadUserCurrentSubscriptionApi,
  loadUserCurrentSubscriptionApiFailure,
  loadUserCurrentSubscriptionApiSuccess,
} from "src/app/core/store/plan/plan.actions";
import { Role, ROLES } from "../constants/roles.constant";
import { DialogService } from "./dialog.service";
import { SignInErrorComponent } from "src/app/session/signin/sign-in-error/sign-in-error.component";
import { MasqueradeUserResponse } from "src/app/core/models/masquerade.model";
import { CurrentUser, userHasRoleplayAccess } from "src/app/core/models/user.model";
import { AnalyticsService } from "src/app/analytics.service";
import { MasqueradeService } from "./masquerade.service";
import { CAMPAIGN_REDIRECT_STORAGE_KEY } from "../constants/campaign.constants";
import { ONBOARDING_REDIRECT_STORAGE_KEY } from "../constants/onboarding-redirect.constants";
import { COUPON_REDIRECT_STORAGE_KEY } from "../constants/coupon-redirect.constants";
import { PendingCouponService } from "./pending-coupon.service";

declare var profitwell: any;

@Injectable()
export class AuthService {
  redirectUrl: string;
  // External redirect for cross-app authentication
  externalRedirectUrl: string | null = null;
  externalRedirectPath: string | null = null;
  externalRedirectState: string | null = null;

  constructor(
    private readonly dialogRef: VngDialogService,
    public dialog: DialogService,
    private readonly nav: NavigationService,
    private readonly dataService: DataService,
    private readonly loaderService: LoadingService,
    private readonly sharedData: SharedDataService,
    private readonly socialService: SocialAuthService,
    private readonly notification: NotificationService,
    private readonly extensionService: ExtensionService,
    private readonly stripeService: StripeService,
    public notifyService: NotifyService,
    private readonly route: ActivatedRoute,
    private readonly store: Store<PlanState>,
    private readonly analyticsService: AnalyticsService,
    private readonly masqueradeService: MasqueradeService,
    private readonly pendingCoupon: PendingCouponService
  ) {
    // Setting default route to navigate after login
    this.redirectUrl = "/flyboard";
    this.route.queryParams.subscribe((params) => {
      // Handle external redirect for cross-app authentication
      if (params.externalRedirect) {
        this.externalRedirectUrl = params.externalRedirect;
        this.externalRedirectPath = params.path || "/";
        this.externalRedirectState = params.state || null;
      }

      if (params.redirect) {
        if (params.redirect === "starter") {
          this.redirectUrl = "/new-plan/Starter";
        }
        if (params.redirect == "yearly-starter") {
          this.redirectUrl = "/new-plan/YearlyStarter";
        }

        if (params.redirect == "growth") {
          this.redirectUrl = "/new-plan/Growth";
        }

        if (params.redirect == "yearly-growth") {
          this.redirectUrl = "/new-plan/YearlyGrowth";
        }

        if (params.redirect == "sales-pro") {
          this.redirectUrl = "/new-plan/SalesPro";
        }

        if (params.redirect == "yearly-sales-pro") {
          this.redirectUrl = "/new-plan/YearlySalesPro";
        }

        if (params.redirect == "freemium") {
          this.redirectUrl = "/flyboard";
        }
      }
    });
  }

  /**
   * Checks if there's a pending external redirect and performs it
   * Returns true if redirect was performed, false otherwise
   */
  handleExternalRedirectIfNeeded(): boolean {
    if (!this.externalRedirectUrl || !this.isAllowedExternalRedirect(this.externalRedirectUrl)) {
      return false;
    }

    // RolePlay is on for everyone EXCEPT a company that has switched it off
    // (see userHasRoleplayAccess) — a signin that started there must not bounce
    // such an account back with a fresh token. Fall through to the normal
    // post-login navigation instead.
    if (
      this.isRoleplayExternalRedirect(this.externalRedirectUrl) &&
      !userHasRoleplayAccess(this.userDetails?.user_details)
    ) {
      this.notification.toast("RolePlay has been turned off for your company.");
      this.clearExternalRedirect();
      return false;
    }

    const accessToken = this.sharedData.getAttribute("accessToken");
    const refreshToken = this.sharedData.getAttribute("refreshToken");
    const path = this.externalRedirectPath || "/";

    const separator = this.externalRedirectUrl.includes("?") ? "&" : "?";
    const encodedPath = encodeURIComponent(path);
    // Echo the caller's one-time `state` nonce so the target app can bind
    // this token handoff to the redirect it initiated (roleplay rejects
    // handoffs whose state doesn't match).
    const stateParam = this.externalRedirectState
      ? `&state=${encodeURIComponent(this.externalRedirectState)}`
      : "";
    // Admin/CMC pass a bare hostname while roleplay passes its full origin —
    // only prefix a scheme when one isn't already present.
    const targetBase = /^https?:\/\//i.test(this.externalRedirectUrl)
      ? this.externalRedirectUrl
      : `https://${this.externalRedirectUrl}`;
    hardNavigation.navigate(`${targetBase}${separator}token=${accessToken}&refresh=${refreshToken}&path=${encodedPath}${stateParam}`);

    this.clearExternalRedirect();
    return true;
  }

  private clearExternalRedirect(): void {
    this.externalRedirectUrl = null;
    this.externalRedirectPath = null;
    this.externalRedirectState = null;
  }

  /** True when the pending external redirect targets the FlyMSG Roleplay app. */
  private isRoleplayExternalRedirect(url: string): boolean {
    const roleplayDomains = [
      (environment as any).ROLEPLAY_APP_DOMAIN,
      (environment as any).ROLEPLAY_APP_URL,
    ].filter(Boolean);
    return roleplayDomains.some((domain: string) => url.startsWith(domain) || url.includes(domain));
  }

  /**
   * Validates that the external redirect URL is one of the allowed domains
   */
  private isAllowedExternalRedirect(url: string): boolean {
    const allowedDomains = this.getAllowedExternalDomains();
    return allowedDomains.some((domain) => url.includes(domain));
  }

  /**
   * Gets the list of allowed external redirect domains
   */
  private getAllowedExternalDomains(): string[] {
    const domains: string[] = [];

    // Add configured domains from environment
    if ((environment as any).ADMIN_APP_DOMAIN) {
      domains.push((environment as any).ADMIN_APP_DOMAIN);
    }
    if ((environment as any).CMC_APP_DOMAIN) {
      domains.push((environment as any).CMC_APP_DOMAIN);
    }
    if ((environment as any).ROLEPLAY_APP_DOMAIN) {
      domains.push((environment as any).ROLEPLAY_APP_DOMAIN);
    }
    if ((environment as any).ROLEPLAY_APP_URL) {
      domains.push((environment as any).ROLEPLAY_APP_URL);
    }

    // Fallback domains for production
    domains.push("admin.vengreso.com", "cmc.vengreso.com");

    // Add localhost for development
    if (!environment.production) {
      domains.push("localhost:4201", "localhost:4202", "localhost");
    }

    return domains;
  }

  /**
   * Reads and clears a campaign redirect stashed by AuthGuardService before
   * it bounced a logged-out user to /session/signin. Returns null if the
   * user didn't arrive via a campaign deep-link.
   */
  consumeCampaignRedirectUrl(): string | null {
    const url = sessionStorage.getItem(CAMPAIGN_REDIRECT_STORAGE_KEY);
    if (url) {
      sessionStorage.removeItem(CAMPAIGN_REDIRECT_STORAGE_KEY);
    }
    return url;
  }

  /**
   * Reads and clears an onboarding-modal-open redirect stashed by
   * AuthGuardService before it bounced a logged-out user to /session/signin.
   * Returns null if the user didn't arrive via one of the legacy onboarding
   * URLs (/onboarding, /onboarding0-9, /retakeonboarding). Takes the same
   * priority as consumeCampaignRedirectUrl() — honored regardless of the
   * user's onboarding-pending status, since clicking one of these URLs
   * always means "open the modal now".
   */
  consumeOnboardingRedirectUrl(): string | null {
    const url = sessionStorage.getItem(ONBOARDING_REDIRECT_STORAGE_KEY);
    if (url) {
      sessionStorage.removeItem(ONBOARDING_REDIRECT_STORAGE_KEY);
    }
    return url;
  }

  /**
   * Reads and clears a `?coupon=` redirect (the FlyGrammar offer link)
   * stashed by AuthGuardService before it bounced a logged-out user to
   * /session/signin. Returns null if the user didn't arrive via a coupon
   * deep-link. Consumed after the campaign and onboarding redirects (issue
   * #592, contracts pinned) — those two remain the higher-priority intent
   * when a deep-link combines them; carrying the coupon through to a
   * campaign or onboarding destination is out of scope for this item.
   */
  consumeCouponRedirectUrl(): string | null {
    const url = sessionStorage.getItem(COUPON_REDIRECT_STORAGE_KEY);
    if (url) {
      sessionStorage.removeItem(COUPON_REDIRECT_STORAGE_KEY);
    }
    return url;
  }

  /**
   * Drops a stashed `?coupon=` redirect without consuming it into a
   * navigation. Called when a higher-priority redirect (campaign or
   * onboarding) wins the same sign-in/sign-up round trip, and on sign-out.
   * Left in place, a losing stash survives to hijack a *later* sign-in in
   * the same tab — possibly by a different account (issue #592 F-5, an
   * orchestrator default pending human confirmation; see the Fix report).
   */
  dropCouponRedirect(): void {
    try {
      sessionStorage.removeItem(COUPON_REDIRECT_STORAGE_KEY);
    } catch {
      // sessionStorage unavailable — nothing to drop.
    }
    this.pendingCoupon.clear();
  }

  /**
   * Durably stashes a `coupon_redirect_url` value into PendingCouponService
   * before navigating to it, so the code survives even if that navigation
   * is itself intercepted (e.g. an unverified account bounced to
   * /session/verify-email before /pricing ever mounts — issue #592 F-3).
   * Exposed for callers outside this service (SigninComponent's LinkedIn
   * callback) that also consume `consumeCouponRedirectUrl()`.
   */
  captureCouponRedirect(url: string): void {
    this.pendingCoupon.captureFromUrl(url);
  }

  getAccessToken() {
    const maskUser = this.masqueradeUser;
    if (maskUser) {
      return maskUser.access_token;
    }

    return this.sharedData.getAttribute("accessToken");
  }

  login(userData: any) {
    if (userData.email) userData.email = userData.email.toLowerCase();
    const url = "v1/user/auth/login";
    this.dataService
      .post({
        url,
        data: { value: userData, noCredentials: true },
        isLoader: true,
      })
      .subscribe({
        next: async (response: any) => {
          const result = response.result;
          if (result) {
            this.analyticsService.track("login_email", {
              email: userData.email,
            });

            //Check if user with temporary password hasn't changed it
            const hasTemporaryPassword = result.user_details.hasOwnProperty("temp_password");

            if (hasTemporaryPassword) {
              await this.saveTemporaryUserDetail(result);
              if (result.user_details.is_poc) {
                window.open(`${environment.ADMIN_APP_URL}`, "_blank");
              }
              this.nav.navigateTo(["/session/sign-in/set-new-password"]);
              return;
            }

            await this.saveUserDetail(result);
            this.extensionService.sendLoginMessage(url, result);

            // Handle external redirect for cross-app authentication (e.g., from admin or CMC app)
            if (this.handleExternalRedirectIfNeeded()) {
              profitwell("start", { user_email: result.user_details.email });
              return;
            }

            if (this.redirectUrl.includes("instancyssorequested=true")) {
              this.nav.navigateTo(["/flyboard"], {
                queryParams: { instancyssorequested: "true" },
              });
              return;
            }

            // Campaign deep-links take priority over onboarding: a returning
            // user who hasn't finished onboarding yet should still land back
            // on the campaign immediately, not be routed through onboarding.
            const campaignRedirect = this.consumeCampaignRedirectUrl();
            if (campaignRedirect) {
              // A losing coupon stash must not survive to hijack a later
              // sign-in in this tab (issue #592 F-5).
              this.dropCouponRedirect();
              this.nav.navigateByFullUrl(campaignRedirect);
              profitwell("start", { user_email: result.user_details.email });
              return;
            }

            // A signed-out click on a legacy onboarding URL also takes
            // priority — it always means "open the modal now", regardless of
            // this account's onboarding-pending status.
            const onboardingRedirect = this.consumeOnboardingRedirectUrl();
            if (onboardingRedirect) {
              // Same reason as the campaign branch above (issue #592 F-5).
              this.dropCouponRedirect();
              this.nav.navigateByFullUrl(onboardingRedirect);
              profitwell("start", { user_email: result.user_details.email });
              return;
            }

            // A signed-out click on the FlyGrammar offer link also takes
            // priority over the onboarding-pending default — the user
            // clicked through specifically to redeem the offer.
            const couponRedirect = this.consumeCouponRedirectUrl();
            if (couponRedirect) {
              // Durably stash the code before navigating: if this account
              // turns out to be unverified, AuthGuardService bounces this
              // navigation to /session/verify-email before /pricing ever
              // mounts to capture it itself (issue #592 F-3).
              this.pendingCoupon.captureFromUrl(couponRedirect);
              this.nav.navigateByFullUrl(couponRedirect);
              profitwell("start", { user_email: result.user_details.email });
              return;
            }

            if (result.user_details?.onboarding == "pending" || !result?.user_details?.onboarding) {
              if (this.redirectUrl.includes("activate_trial=true")) {
                this.stripeService.startSalesProTrial().subscribe({
                  next: (response: any) => {
                    this.nav.navigateTo([`/flyboard`]);
                    return;
                  },
                  error: (error) => { },
                });
              }
              this.nav.navigateTo([`/flyboard`]);
            } else {
              if (this.redirectUrl.includes("activate_trial=true")) {
                this.nav.navigateTo(["/new-plan/SalesPro"], {
                  queryParams: { activate_trial: "true" },
                });
                return;
              }
              this.nav.navigateTo([this.redirectUrl]);
            }

            profitwell("start", { user_email: result.user_details.email });
          } else {
            if (response.error) {
              this.notification.toast(response.error, "danger");
            }
          }
        },
        error: (error) => {
          if (error.status == 422) {
            if (error?.error?.code == "EXPIRED_LOGIN") {
              this.nav.navigateTo(["/session/expired-login"], {
                queryParams: { email: userData.email },
              });
              return;
            }

            if (error?.error?.code == "DEACTIVATED") {
              this.dialog.openDialogComponent(
                SignInErrorComponent,
                {
                  admin_email: error?.error?.admin_email,
                },
                "500px"
              );
            }
          }

          this.notification.toast(error?.error?.error, "danger");
        },
      });
  }

  isLoggedIn() {
    return Boolean(this.accessToken && this.userDetails);
  }

  refreshAccessToken(): Observable<any> {
    const data = { refresh_token: this.getRefreshToken() };
    return this.dataService.post({
      url: "v1/user/auth/refresh",
      data,
      isLoader: false,
      ignoreErrors: true,
    });
  }

  getRefreshToken() {
    return this.sharedData.getAttribute("refreshToken");
  }

  getAccessTokenFromExtension(): Promise<string | null> {
    return this.extensionService.getAccessTokenFromExtension();
  }

  redirectAfterLogout(sendExtensionLogout: boolean = false, reason?: "session_expired" | "manual") {
    var browser = detect();
    var browser_type;
    if (browser && (browser?.name == "edge" || browser?.name == "edge-chromium")) {
      browser_type = "edge";
    } else {
      browser_type = browser?.name;
    }
    this.dialogRef.closeAll();
    if (sendExtensionLogout) {
      this.extensionService.sendLogoutMessage();
    }
    this.sharedData.clear();
    this.stripeService.removePlanInformation();
    // Store browser type

    if (browser_type) {
      this.sharedData.setAttribute("browser_type", browser_type);
    }

    // When the redirect is triggered by an expired/invalid session (refresh
    // token rejected, extension fallback empty), tell the user explicitly via
    // both a toast and a query param the sign-in screen can render as a
    // sticky notice. Without this the user just sees a blank /signin and has
    // no idea why they were kicked out.
    if (reason === "session_expired") {
      try {
        this.notification?.toast(
          "Your session expired. Please sign in again.",
          "OK",
          5000
        );
      } catch {
        // NotificationService may not be ready during very early app boot.
      }
      this.nav.navigateTo(["/session/signin"], { queryParams: { expired_session: 1 } });
    } else {
      this.nav.navigateTo(["/session/signin"]);
    }
    if (environment.production) {
      // Reset identity on heap before logout
      window.heap.resetIdentity();
    }
  }

  getUserRole(): Role[] {
    const userRoles = this.userDetails?.user_details?.role;

    const roles: Role[] = [];

    // role arrives as a comma-separated string on login, but masquerade
    // responses may deliver a string[] — normalize both.
    const roleList = Array.isArray(userRoles) ? userRoles : (userRoles?.split(",") ?? []);
    roleList.forEach((role: Role) => {
      if (ROLES.includes(role)) {
        roles.push(role);
      }
    });

    if (roles.length === 0) {
      roles.push("User");
    }

    return roles;
  }

  get companyId(): string {
    if (this.masqueradeService.isMasquerading()) {
      const company = this.masqueradeService.getMasqueradeCompanyInfo();
      if (company.id) {
        return company.id;
      }
    }

    const user_details = this.userDetails.user_details;

    return user_details.company_id;
  }

  /**
   * True for a company-paid seat whose invitation has been accepted —
   * their plan is managed by the company account, so pricing/billing
   * self-service (Upgrade/Edit Plan CTAs, FlyShare Rewards, the /pricing
   * page) does not apply to them. `status === "Invited"` is excluded: an
   * invited-but-not-yet-accepted user still has their own individual
   * billing until they accept, so they keep self-service access.
   *
   * This is the single source of truth for that check — settings.component
   * and the pricing route guard both delegate to it (issue #410) instead of
   * re-deriving `company_id && status !== "Invited"` locally.
   */
  get isCompanyEndUser(): boolean {
    const user_details = this.userDetails?.user_details;

    return Boolean(user_details?.company_id) && user_details?.status !== "Invited";
  }

  getUserCompanySlug(): string {
    if (this.masqueradeService.isMasquerading()) {
      const company = this.masqueradeService.getMasqueradeCompanyInfo();
      if (company.slug) {
        return company.slug;
      }
    }

    const { company, user_details } = this.userDetails;

    return company ?? user_details.company.slug ?? "company";
  }

  showVengresoAdminSection(): boolean {
    const roles = this.getUserRole();

    return roles.includes("Vengreso Admin");
  }

  showAdminSection(): boolean {
    const roles = this.getUserRole();

    return (
      roles.some((role) => ["Group Admin", "Reporting Admin", "Global Admin"].includes(role)) &&
      this.userDetails.user_details.status !== "Invited"
    );
  }

  logout(logoutReason: any = {}) {
    const userDetail = this.userDetails;
    if (userDetail.provider) {
      this.socialService.signOut().then((response: any) => { });
    }
    this.sharedData.deleteAttribute("email_to_verify");
    this.sharedData.deleteAttribute("timer_to_resend");
    // A stashed coupon redirect must not survive sign-out to hijack the
    // next sign-in in this tab — possibly by a different account (issue
    // #592 F-5).
    this.dropCouponRedirect();

    this.dataService.post({ url: "v1/user/auth/logout", data: logoutReason }).subscribe((response: any) => {
      if (response && response.result) {
        this.redirectAfterLogout(true);
      }
    });

    this.redirectUrl = "/flyboard";
  }

  getNewTemporaryPassword(email: string) {
    return this.dataService.post({
      url: "v1/users/resend-temporary-password",
      data: { email },
      isLoader: true,
    });
  }

  resetTemporaryPassword(data: any) {
    return this.dataService.post({
      url: "v1/users/reset-invited-user-password",
      data: data,
      isLoader: true,
    });
  }

  resetPassword(userData: any) {
    const data = {
      value: userData,
      noCredentials: true,
    };

    this.dataService.post({ url: "password/email", data }).subscribe(
      (result: any) => {
        this.nav.navigateTo(["/login"]);
        this.notification.toast("Check Your Mail! Click on reset link.");
      },
      (error) => { }
    );
  }

  registration(val: any) {
    if (val.email) val.email = val.email.toLowerCase();
    this.loaderService.start();
    this.dataService.post({ url: "v1/user/auth/register", data: val, isLoader: false }).subscribe({
      next: async (response: any) => {
        // The user is logged in after the sign up.
        await this.saveUserDetail(response.result);

        // Handle external redirect for cross-app authentication (e.g., from admin or CMC app)
        if (this.handleExternalRedirectIfNeeded()) {
          this.loaderService.stop();
          return;
        }

        if (this.redirectUrl.includes("activate_trial=true")) {
          this.stripeService.startSalesProTrial().subscribe({
            next: (response: any) => {
              this.nav.navigateTo([`/flyboard`]);
              return;
            },
            error: (error) => { },
          });
        }
        // Track the affiliate signup event
        if (typeof (window as any).gr === "function") {
          (window as any).gr("track", "conversion", {
            email: val.email.toLowerCase(),
          });
        }

        if (response.result?.is_company_poc === true) {
          window.open(`${environment.ADMIN_APP_URL}`, "_self");
          this.loaderService.stop();
          return;
        }

        // A brand-new signup arriving via a campaign deep-link goes straight
        // there instead of the standard onboarding wizard — the campaign's
        // own step 1 re-verifies the extension anyway, and the offer is
        // meant to be instant, not gated behind a 9-step wizard.
        const campaignRedirect = this.consumeCampaignRedirectUrl();
        if (campaignRedirect) {
          // A losing coupon stash must not survive to hijack a later sign-in
          // in this tab (issue #592 F-5).
          this.dropCouponRedirect();
          this.nav.navigateByFullUrl(campaignRedirect);
          this.loaderService.stop();
          return;
        }

        // A brand-new signup arriving via the FlyGrammar offer link goes
        // straight there too, for the same reason as the campaign branch
        // above (issue #592 — the offer survives sign-up, not only sign-in).
        const couponRedirect = this.consumeCouponRedirectUrl();
        if (couponRedirect) {
          // Durably stash the code before navigating: a brand-new signup is
          // always unverified at this point, so AuthGuardService bounces
          // this navigation to /session/verify-email before /pricing ever
          // mounts to capture it itself — without this the code was
          // silently lost (issue #592 F-3). VerifyEmailComponent reads it
          // back out and routes to /pricing once verification completes;
          // PendingCouponService.clearIfOutsideFunnel() treats
          // /session/verify-email as part of the funnel so it survives that
          // hop.
          this.pendingCoupon.captureFromUrl(couponRedirect);
          this.nav.navigateByFullUrl(couponRedirect);
          this.loaderService.stop();
          return;
        }

        this.nav.navigateTo([`/flyboard`]);
        this.loaderService.stop();
      },
      error: ({ error }: any) => {
        this.loaderService.stop();
        let errors = [];
        forEach(error?.error_list, (array, key) => {
          errors = errors.concat(array);
        });
        if (errors.length === 1 && errors[0] === "The email has already been taken.") {
          this.notification.toast(
            "You already have an account under this email address. Please sign-in instead.",
            "ok",
            4000
          );
          return;
        }
        this.notification.toast(error?.result?.message ?? "Something went wrong", "ok", 4000);
      },
    });
  }

  async saveTemporaryUserDetail(result: any) {
    this.sharedData.setAttribute("temporaryUserDetails", result);
  }

  async saveUserDetail(result: any) {
    this.sharedData.setAttribute("userDetails", result);
    if (result.access_token) {
      this.sharedData.setAttribute("accessToken", result.access_token);
    }
    if (result.refresh_token) {
      this.sharedData.setAttribute("refreshToken", result.refresh_token);
    }
    if (this.masqueradeUser) {
      this.sharedData.setAttribute(this.masqueradeUser.user_details.id, result);
    }
    this.store.dispatch(loadUserCurrentSubscriptionApi({}));
    this.refreshUserFeatures();
  }

  /**
   * Fetches the per-user feature map from `GET v2/meta-data` and merges its
   * `user_features` onto the cached user record (issue #559), so
   * `userHasNotificationCenterAccess` (and any future feature-map consumer)
   * can read it synchronously off `userDetails.user_details` the same way
   * `is_beta` was read before. Called from every login path via
   * `saveUserDetail`, and once at app bootstrap
   * (`AppComponent.ngOnInit`) to also refresh sessions that were already
   * signed in before this field existed on their cached record.
   *
   * Fire-and-forget by design: on failure the cached record simply keeps
   * whatever `user_features` it already has (or none), which the gate
   * already treats as "not entitled" — never a crash, never a blocking
   * call. MANDATORY `ignoreErrors: true`: without it, `DataService.
   * handleError` intercepts any 5xx from this call and hard-navigates to
   * `/session/error` (or opens the DEACTIVATED dialog on a 403) — a
   * background feature-map refresh must never be able to throw a
   * logged-in user out of the app on bootstrap or right after login.
   * Skipped during masquerade: the masquerade user record is served from
   * a separate session-only store that this method doesn't write to (see
   * `userDetails`'s masquerade branch), so the fetch would merge into a
   * cache slot nothing reads — same as `is_beta` being absent from
   * `MasqueradeUserResponse.user_details` today.
   */
  refreshUserFeatures(): void {
    if (this.masqueradeUser) {
      return;
    }

    this.dataService.get({ url: "v2/meta-data", isLoader: false, ignoreErrors: true }).subscribe({
      next: (response: any) => {
        const userFeatures = response?.result?.user_features;
        const current = this.userDetails;
        if (!userFeatures || !current) {
          return;
        }

        this.sharedData.setAttribute("userDetails", {
          ...current,
          user_details: {
            ...current.user_details,
            user_features: userFeatures,
          },
        });
        this.notifyService.notifyAboutChange();
      },
      error: () => {
        // Ignore: the gate treats a missing/failed fetch as not entitled.
      },
    });
  }

  updateUserDetail() {
    this.dataService.get({ url: "v1/user", isLoader: false }).subscribe((response: any) => {
      if (response?.result) {
        let previousUserDetail = this.userDetails;
        let { is_password_set, ...userDetails } = response.result;
        let updatedUserDetail = {
          ...previousUserDetail,
          user_details: userDetails,
        };

        this.saveUserDetail(updatedUserDetail);
        this.notifyService.notifyAboutChange();
      }
    });
  }

  verifyEmail(data: any) {
    return this.dataService.post({
      url: "v1/user/auth/email/verify",
      data: data,
      isLoader: true,
      ignoreErrors: true,
    });
  }

  signInWithGoogle(idToken: string, referral_key?: string): void {
    const url = "v1/user/auth/login/google";

    this.loaderService.start();

    let data: any = { token: idToken };
    if (referral_key) {
      data.referral_key = referral_key;
    }

    this.dataService
      .post({
        url,
        data,
        isLoader: false,
      })
      .subscribe({
        next: async (response: any) => {
          await this.saveUserDetail(response.result);
          this.extensionService.sendLoginMessage(url, response.result);

          // Handle external redirect for cross-app authentication (e.g., from admin or CMC app)
          if (this.handleExternalRedirectIfNeeded()) {
            this.loaderService.stop();
            return;
          }

          if (response.result?.is_company_poc === true) {
            window.open(`${environment.ADMIN_APP_URL}`, "_self");
            this.loaderService.stop();
            return;
          }

          if (this.redirectUrl.includes("instancyssorequested=true")) {
            this.nav.navigateTo(["/flyboard"], {
              queryParams: { instancyssorequested: "true" },
            });
            this.loaderService.stop();
            return;
          }

          // Campaign deep-links take priority over onboarding: a returning
          // user who hasn't finished onboarding yet should still land back
          // on the campaign immediately, not be routed through onboarding.
          const campaignRedirect = this.consumeCampaignRedirectUrl();
          if (campaignRedirect) {
            // A losing coupon stash must not survive to hijack a later
            // sign-in in this tab (issue #592 F-5).
            this.dropCouponRedirect();
            this.nav.navigateByFullUrl(campaignRedirect);
            this.loaderService.stop();
            return;
          }

          // A signed-out click on a legacy onboarding URL also takes
          // priority — it always means "open the modal now", regardless of
          // this account's onboarding-pending status.
          const onboardingRedirect = this.consumeOnboardingRedirectUrl();
          if (onboardingRedirect) {
            // Same reason as the campaign branch above (issue #592 F-5).
            this.dropCouponRedirect();
            this.nav.navigateByFullUrl(onboardingRedirect);
            this.loaderService.stop();
            return;
          }

          // A signed-out click on the FlyGrammar offer link also takes
          // priority over the onboarding-pending default — see login() for
          // the same rule (issue #592).
          const couponRedirect = this.consumeCouponRedirectUrl();
          if (couponRedirect) {
            // Durably stash the code before navigating — see login() for
            // the same reason (issue #592 F-3).
            this.pendingCoupon.captureFromUrl(couponRedirect);
            this.nav.navigateByFullUrl(couponRedirect);
            this.loaderService.stop();
            return;
          }

          if (response.result.user_details?.onboarding == "pending" || !response.result.user_details?.onboarding) {
            if (this.redirectUrl.includes("activate_trial=true")) {
              this.stripeService.startSalesProTrial().subscribe({
                next: (response: any) => {
                  this.nav.navigateTo([`/flyboard`]);
                  this.loaderService.stop();
                  return;
                },
                error: (error) => { },
              });
            }
            this.nav.navigateTo([`/flyboard`]);
          } else {
            if (this.redirectUrl.includes("activate_trial=true")) {
              this.nav.navigateTo(["/new-plan/SalesPro"], {
                queryParams: { activate_trial: "true" },
              });
              this.loaderService.stop();
              return;
            }
            this.nav.navigateTo([this.redirectUrl]);
          }

          this.loaderService.stop();
        },
        error: (error) => {
          this.loaderService.stop();
          if (error.status == 422) {
            if (error?.error?.code == "DEACTIVATED") {
              this.dialog.openDialogComponent(
                SignInErrorComponent,
                {
                  admin_email: error?.error?.admin_email,
                },
                "500px"
              );
            }
          }
          this.notification.toast(error?.error?.error, "danger");
        },
      });
  }

  signInWithLinkedIn(): void {
    hardNavigation.navigate(this.apiURL + "v1/linkedin/redirect");
  }

  acceptCompanyInvitation(company_id: string, user_id: string) {
    return this.dataService
      .post({
        url: `v1/users/${user_id}/accept-company-invitation`,
        data: { company_id },
        isLoader: true,
      })
      .pipe(
        map((response: any) => {
          this.store.dispatch(loadUserCurrentSubscriptionApi({ refetchFromApi: true }));
          const { invited_to_company: _itc, invited_to_company_by_admin: _itcba, ...restUserDetails } = this.userDetails?.user_details ?? {};
          const user = {
            ...this.userDetails,
            user_details: {
              ...restUserDetails,
              ...response?.result,
            },
          };

          this.saveUserDetail(user);
          this.notifyService.notifyAboutChange({
            planObj: response?.result?.plan,
          });

          if (user.user_details.is_poc) {
            // navigate to ADMIN APP home
            window.open(`${environment.ADMIN_APP_URL}`, "_blank");
          } else {
            location.reload();
          }

          return response;
        }),
        catchError((error: any) => {
          // This catchError handles errors that occurred before the map.
          return throwError(() => error);
        })
      );
  }

  /**
   * `completedStep` is the flymsg-be#759 extension: an optional per-step key
   * (see OnboardingStepKey) marking that ONE step as genuinely complete,
   * additive to the legacy `step`/`max_step` position fields so old backend
   * deploys keep working unchanged (issue #523 — the FE must tolerate the
   * backend not having #759 yet). When the response DOES carry the new
   * `onboarding_steps_completed`/`onboarding_percentage` fields they're
   * merged in; otherwise `user_details` is left to whatever
   * OnboardingProgressService derived/tracked locally.
   */
  patchOnboarding(
    user_id: string,
    step: number,
    maxStep: number,
    tooltip_learning_resources_showed?: boolean,
    isLoader = true,
    onboardingv2_presented?: boolean,
    completedStep?: string
  ) {
    return this.dataService
      .post({
        url: `v1/users/${user_id}/update-onboarding`,
        data: {
          step,
          tooltip_learning_resources_showed,
          max_step: maxStep - 1,
          onboardingv2_presented,
          completed_step: completedStep,
        },
        isLoader,
      })
      .pipe(
        map((response: any) => {
          const currentUser = { ...this.userDetails };
          currentUser.user_details = { ...currentUser.user_details };
          currentUser.user_details.onboardingv2_step = step;
          currentUser.user_details.onboardingv2_completed = step > maxStep;

          if (tooltip_learning_resources_showed !== undefined) {
            currentUser.user_details.tooltip_learning_resources_showed = tooltip_learning_resources_showed;
          }

          if (onboardingv2_presented !== undefined) {
            currentUser.user_details.onboardingv2_presented = onboardingv2_presented;
          }

          const result = response?.result ?? response;
          if (result && Array.isArray(result.onboarding_steps_completed)) {
            currentUser.user_details.onboarding_steps_completed = result.onboarding_steps_completed;
          }
          if (result && typeof result.onboarding_percentage === "number") {
            currentUser.user_details.onboarding_percentage = result.onboarding_percentage;
          }

          this.saveUserDetail(currentUser);
          return response;
        }),
        catchError((error: any) => {
          // This catchError handles errors that occurred before the map.
          return throwError(() => error);
        })
      );
  }

  rejectCompanyInvitation(company_id: string, user_id: string) {
    return this.dataService
      .post({
        url: `v1/users/${user_id}/reject-company-invitation`,
        data: { company_id },
        isLoader: true,
      })
      .pipe(
        map((response: any) => {
          this.store.dispatch(loadUserCurrentSubscriptionApi({ refetchFromApi: true }));
          const { invited_to_company: _itc, invited_to_company_by_admin: _itcba, ...restUserDetails } = this.userDetails?.user_details ?? {};
          const user = {
            ...this.userDetails,
            user_details: {
              ...restUserDetails,
              ...response?.result,
            },
          };

          this.saveUserDetail(user);
          this.notifyService.notifyAboutChange({
            planObj: response?.result?.plan,
          });

          return response;
        }),
        catchError((error: any) => {
          // This catchError handles errors that occurred before the map.
          return throwError(() => error);
        })
      );
  }

  // REMOVED: masqueradeAsCompany() - replaced by MasqueradeService
  // REMOVED: masqueradeAsUser() - replaced by MasqueradeService

  reloadUserPlanDetailsOnFirstMasqueradeAccess() {
    this.stripeService.getCurrentPlan().subscribe((planInfo: any) => {
      sessionStorage.setItem("fms-masquerade-user-current-reloaded", "true");

      return loadUserCurrentSubscriptionApiSuccess({ payload: planInfo });
    });
  }

  // Delegate to MasqueradeService for all masquerade-related getters
  get onUserMasqueradeSession(): boolean {
    return this.masqueradeService.isMasquerading();
  }

  get onUserMasqueradeSessionCurrentReloaded(): boolean {
    return !!sessionStorage.getItem("fms-masquerade-user-current-reloaded");
  }

  get onCompanyMasqueradeSession(): string {
    const company = this.masqueradeService.getMasqueradeCompanyInfo();
    return company.slug || "";
  }

  get getMasqueradeCompanyName(): string {
    const company = this.masqueradeService.getMasqueradeCompanyInfo();
    return company.name || "";
  }

  get masqueradeUser(): MasqueradeUserResponse | null {
    return this.masqueradeService.getMasqueradeUser();
  }

  get accessToken(): string {
    if (this.masqueradeUser) {
      return this.masqueradeUser.access_token;
    }

    return this.sharedData.getAttribute("accessToken");
  }

  get refreshToken(): string | null {
    if (this.masqueradeUser) {
      return null;
    }

    return this.sharedData.getAttribute("refreshToken");
  }

  /**
   * The signed-in user's auth wrapper (see {@link CurrentUser}).
   * During masquerade this is the narrower MasqueradeUserResponse flowing
   * through the same accessor — it overlaps on every field consumers read
   * (access_token, user_details.*, company slug) but has no refresh_token.
   */
  get userDetails(): CurrentUser | null {
    if (this.masqueradeUser) {
      return this.masqueradeUser as unknown as CurrentUser;
    }

    return this.sharedData.getAttribute<CurrentUser>("userDetails") ?? null;
  }

  // REMOVED: endMasqueradeSession() - replaced by MasqueradeService.endMasquerade()

  // Issue #564 (round 2): sessionConfirmed$/confirmSession() moved to the
  // root-provided SessionConfirmationService — AuthService itself is
  // module-provided (CoreModule), so it is NOT a singleton (a lazy module
  // importing CoreModule, e.g. DashboardModule, gets its own instance).
  // See session-confirmation.service.ts for the full explanation.

  private get apiURL(): string {
    return environment.API_URL;
  }

  handleError() {
    this.nav.navigateTo(["/session/error"]);
  }
}
